import React, { useEffect, useState } from 'react';
import useAolSaveHandler from '../../../../app/useAolSaveHandler';
import { NoonReportSectionEnum, NoonReportTypeEnum } from '../../../../types/ViewModel/AolEnums';
import { AolDataItem } from '../../../../types/ViewModel/AolItem';
import { ValidationItem } from '../../../../types/ViewModel/ValidationItemType';
import { hasInvalidFields, hasMissingValues } from '../../../../utils/aolFieldsHelper';
import { ErrorText } from '../../../shared/error-text/ErrorText.style';
import OdfjellInputComponent, {
    DateInputChangeData,
    InputChangeData,
    TimeInputChangeData,
} from '../../../common/odfjell-input-component/OdfjellInputComponent';
import { EnginesDataVM } from '../EnginesSection';
import { EngineWrapper } from '../EnginesSection.style';
import { SubSectionHeader } from '../../aol-container/AolContainer.style';
import { roundTo2Decimals } from '../../../../utils/numberHelper';
import { prettifyDate } from 'odfjell-date-time-utils/dateTimeFormatter';
import { isDateAfter } from 'odfjell-date-time-utils/dateTimeUtils';

type Props = {
    enginesDataVM: EnginesDataVM;
    onChange: () => void;
    aolDataItem: AolDataItem;
    hasEmptyValues;
    isReadOnly: boolean;
};

const EconomizerSectionComponent: React.FC<Props> = ({ enginesDataVM, onChange, aolDataItem, hasEmptyValues, isReadOnly }) => {
    const { saveAolEvent } = useAolSaveHandler();
    const [hasEmptyValuesLocal, setHasEmptyValuesLocal] = useState(false);

    useEffect(() => {
        if (
            saveAolEvent &&
            saveAolEvent.type === 'save_section' &&
            saveAolEvent.section === NoonReportSectionEnum.Engines &&
            aolDataItem &&
            saveAolEvent.aolDataItem.id === aolDataItem.id
        ) {
            checkOnSave();
        }
    }, [saveAolEvent]);

    useEffect(() => {
        setHasEmptyValuesLocal(false);
    }, [aolDataItem]);

    const getReportDateValidation = (reportDate: Date) => {
        const validation: ValidationItem[] = [
            {
                validationFunction: (date: Date) => date && isDateAfter(date, reportDate, 'day'),
                errorMessage: 'Date cannot be after ' + prettifyDate(reportDate),
            },
        ];

        return validation;
    };

    const onInputChange = (changeObject: InputChangeData) => {
        const name = changeObject.event.target.name;
        const value = changeObject.event.target.type === 'checkbox' ? changeObject.event.target.checked : changeObject.event.target.value;

        if (changeObject.event.target.type === 'checkbox') {
            changeObject.dataObject[name] = value;
        } else {
            changeObject.dataObject[name] = value ? +value : undefined;
        }

        resetHasEmptyValues();
        hasInvalidFields(enginesDataVM, name, changeObject.isValid);
        onChange();
    };

    const onTimeInputChange = (changeObject: TimeInputChangeData) => {
        changeObject.dataObject[changeObject.name] = changeObject.hours;
        resetHasEmptyValues();
        hasInvalidFields(changeObject.dataObject, changeObject.name, changeObject.isValid);
        onChange();
    };

    const onDateInputChange = (changeObject: DateInputChangeData) => {
        changeObject.dataObject[changeObject.name] = changeObject.date;
        resetHasEmptyValues();
        hasInvalidFields(changeObject.dataObject, changeObject.name, changeObject.isValid);
        onChange();
    };

    const resetHasEmptyValues = () => {
        if (hasEmptyValuesLocal) setHasEmptyValuesLocal(false);
    };

    const checkOnSave = () => {
        const ignoreList: string[] = [];
        const economizerData = {
            economizerRunningHours: enginesDataVM.economizerRunningHours,
            isEconomizerSootBlowingEquipmentInOperationalCondition: enginesDataVM.isEconomizerSootBlowingEquipmentInOperationalCondition,
            economizerDateOfLastCleaning: enginesDataVM.economizerDateOfLastCleaning,
            shaftGeneratorData: enginesDataVM.shaftGeneratorData,
        };

        if (aolDataItem.reportType !== NoonReportTypeEnum.Sea) {
            ignoreList.push('economizerRunningHours');
        }
        if (hasMissingValues(economizerData, ignoreList)) {
            setHasEmptyValuesLocal(true);
            hasEmptyValues.current = true;
        } else {
            if (hasEmptyValuesLocal) setHasEmptyValuesLocal(false);
            hasEmptyValues.current = false;
        }
    };

    return (
        <>
            <EngineWrapper>
                <SubSectionHeader>Economizer</SubSectionHeader>
                {aolDataItem.reportType === NoonReportTypeEnum.Sea && (
                    <>
                        <OdfjellInputComponent
                            isReadOnly={isReadOnly}
                            description={'Running hours'}
                            type={'time'}
                            value={enginesDataVM.economizerRunningHours}
                            onTimeInputChange={onTimeInputChange}
                            name={'economizerRunningHours'}
                            dataObject={enginesDataVM}
                            max={roundTo2Decimals(aolDataItem.reportHours)}
                        ></OdfjellInputComponent>
                        <OdfjellInputComponent
                            isReadOnly={isReadOnly}
                            type={'checkbox'}
                            description={'Soot blowing equipment in operational condition'}
                            name={'isEconomizerSootBlowingEquipmentInOperationalCondition'}
                            dataObject={enginesDataVM}
                            value={enginesDataVM.isEconomizerSootBlowingEquipmentInOperationalCondition}
                            onChange={onInputChange}
                            unit={''}
                        />
                    </>
                )}
                <OdfjellInputComponent
                    isReadOnly={isReadOnly}
                    type={'date'}
                    description={'Last cleaning'}
                    name={'economizerDateOfLastCleaning'}
                    dataObject={enginesDataVM}
                    customValidations={getReportDateValidation(aolDataItem.reportDate)}
                    value={enginesDataVM.economizerDateOfLastCleaning}
                    onDateInputChange={onDateInputChange}
                    unit={''}
                />
            </EngineWrapper>
            {hasEmptyValuesLocal && <ErrorText>You must enter all economizer data before saving.</ErrorText>}
        </>
    );
};

export default EconomizerSectionComponent;
